import { useState, useEffect } from 'react';
import { BarChart3, Map, Target, Zap, type LucideIcon } from 'lucide-react';
import { Card, Button, Stat } from '@/shared/ui';
import { VapiCallModal } from '../components/VapiCallModal';
import { VapiCallBanner } from '../components/VapiCallBanner';
import styles from './StrategyOverviewPage.module.css';

const DELIVERABLES: Array<{ icon: LucideIcon; t: string; d: string }> = [
  {
    icon: BarChart3,
    t: 'Market Overview',
    d: 'Demographics, income, growth trends for your service area',
  },
  {
    icon: Map,
    t: 'Competitive Map',
    d: 'Every competitor analyzed — reviews, services, positioning gaps',
  },
  {
    icon: Target,
    t: 'Strategic Insights',
    d: 'Data-backed opportunities matched to your practice goals',
  },
  { icon: Zap, t: 'Activation Plan', d: 'Approved strategies power SEO, ads, and content' },
];

interface Props {
  onStart: () => void;
  starting?: boolean;
  locationId: string;
  locationPhone?: string;
  autoOpenModal?: boolean;
}

export function NotStartedView({
  onStart,
  starting,
  locationId,
  locationPhone,
  autoOpenModal,
}: Props) {
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (autoOpenModal) setModalOpen(true);
  }, [autoOpenModal]);

  return (
    <div className={styles.notStartedWrapper}>
      <div className={styles.engineLabel}>Strategy Engine</div>
      <h1 className={styles.heroTitle}>
        Build Your AI-Powered
        <br />
        Marketing Strategy
      </h1>
      <p className={styles.heroSubtitle}>
        We'll combine your practice data, local market intelligence, and competitive analysis to
        generate a custom growth strategy — ready to execute.
      </p>

      <Card className={styles.cardDeliverables}>
        <div className={styles.deliverableHeading}>What you'll get</div>
        <div className={styles.deliverableGrid}>
          {DELIVERABLES.map((x, i) => {
            const Icon = x.icon;
            return (
              <div key={i} className={styles.deliverableItem}>
                <span className={styles.deliverableIcon}>
                  <Icon size={18} strokeWidth={1.75} aria-hidden />
                </span>
                <div>
                  <div className={styles.deliverableTitle}>{x.t}</div>
                  <div className={styles.deliverableDesc}>{x.d}</div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card className={styles.cardMb20}>
        <div className={styles.statRow}>
          <div className={styles.statCell}>
            <Stat label="Your input" value="~8 min" tone="accent" />
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statCell}>
            <Stat label="AI analysis" value="~2 min" tone="info" />
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statCell}>
            <Stat label="Steps" value="6" tone="info" />
          </div>
        </div>
      </Card>

      <VapiCallBanner locationId={locationId} onOpen={() => setModalOpen(true)} />

      <div className={styles.centerBtn}>
        <Button variant="primary" onClick={onStart} disabled={starting} loading={starting}>
          Start Building Strategy →
        </Button>
      </div>

      <VapiCallModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        locationId={locationId}
        defaultPhone={locationPhone}
      />
    </div>
  );
}
